import './QuizQuestion.css';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchQuiz } from '../../services/quizService';
import { getQuestionById } from '../../services/questionService';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import MultipleCorrectQuestion from './MultipleCorrectQuestion';
import FillInQuestion from './FillInQuestion';
import LinkingQuestion from './LinkingQuestion';
import TimelineQuestion from './TimelineQuestion';
import FindInTextQuestion from './FindInTextQuestion';
import UnsavedChangesModal from '../UnsavedChangesModal/UnsavedChangesModal';

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

const TYPE_MAP = {
    'MULTICHOICE': 'Multichoice',
    'MULTIRESPONSE': 'MultipleCorrect',
    'FILLIN': 'FillIn',
    'FILL_IN': 'FillIn',
    'LINKING': 'Linking',
    'TIMELINE': 'Timeline',
    'FINDINTEXT': 'FindInText',
    'FIND_IN_TEXT': 'FindInText',
};

function mapApiQuestion(q) {
    const type = TYPE_MAP[q.questionType] || q.questionType;
    const hasDirectBooks = (q.readingJournalIds && q.readingJournalIds.length > 0) || !!q.readingJournalId;
    const hasDirectAuthors = q.authorIds && q.authorIds.length > 0;

    const base = {
        id: q.id,
        type,
        questionText: q.title,
        relatedBook: (q.readingJournalIds?.[0]) || q.readingJournalId || q._bookId || null,
        relatedBookTitle: hasDirectBooks ? (q._bookTitle || null) : null,
        relatedAuthorId: hasDirectAuthors ? (q.authorIds?.[0] || null) : null,
        relatedAuthorName: hasDirectAuthors ? (q._authorName || null) : null,
    };

    if (type === 'Multichoice' || type === 'MultipleCorrect') {
        const choices = shuffle((q.choices || []).map((c, i) => ({
            id: String.fromCharCode(97 + i),
            text: c.text,
            note: c.notes || c.note || '',
        })));
        return {
            ...base,
            choices,
            correctAnswer: type === 'Multichoice' && q.correctChoice != null
                ? String.fromCharCode(97 + q.correctChoice)
                : undefined,
            correctAnswers: type === 'MultipleCorrect' && q.correctChoices
                ? q.correctChoices.map(i => String.fromCharCode(97 + i))
                : undefined,
        };
    }

    if (type === 'FillIn') {
        return {
            ...base,
            text: q.text,
            blankPosition: q.blankPosition,
            correctAnswers: q.correctAnswers,
        };
    }

    if (type === 'Linking') {
        // Normalize: backend returns plain strings, components expect {id, text} objects
        const leftItems = (q.leftItems || []).map((item, i) =>
            typeof item === 'string' ? { id: `l${i}`, text: item } : item
        );
        const rightItems = (q.rightItems || []).map((item, i) =>
            typeof item === 'string' ? { id: `r${i}`, text: item } : item
        );
        // Normalize correctPairs: [[int, int], ...] → [{left: "l0", right: "r0"}, ...]
        const correctPairs = (q.correctPairs || []).map(pair =>
            Array.isArray(pair)
                ? { left: `l${pair[0]}`, right: `r${pair[1]}` }
                : pair
        );
        return {
            ...base,
            leftItems: shuffle(leftItems),
            rightItems: shuffle(rightItems),
            correctPairs,
        };
    }

    if (type === 'Timeline') {
        // Normalize: backend returns plain strings, components expect {id, text} objects
        const items = (q.items || []).map((item, i) =>
            typeof item === 'string' ? { id: `i${i}`, text: item } : item
        );
        // Normalize correctOrder: [int, ...] → ["i0", "i1", ...]
        const correctOrder = (q.correctOrder || []).map(order =>
            typeof order === 'number' ? `i${order}` : order
        );
        return {
            ...base,
            items: shuffle(items),
            correctOrder,
        };
    }

    if (type === 'FindInText') {
        return {
            ...base,
            text: q.text,
            correctIndices: q.correctIndices || [],
        };
    }

    return base;
}

function QuizQuestion() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const skipRef = useRef(false);
    const [questions, setQuestions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
        location.state?.startIndex ?? 0
    );
    const [selectedAnswers, setSelectedAnswers] = useState(
        location.state?.selectedAnswers ?? {}
    );

    useEffect(() => {
        const loadQuizQuestions = async () => {
            try {
                setLoading(true);
                setError(null);

                // Already-mapped questions passed from summary navigation
                if (location.state?.questions?.length > 0) {
                    setQuestions(location.state.questions);
                    return;
                }

                // Raw questions provided (e.g. from reading list quiz start)
                if (location.state?.preloadedRawQuestions) {
                    const mapped = location.state.preloadedRawQuestions.map(mapApiQuestion);
                    setQuestions(mapped);
                    return;
                }

                const quiz = await fetchQuiz(id);
                if (!quiz.questionIds || quiz.questionIds.length === 0) {
                    setQuestions([]);
                    return;
                }
                const questionData = await Promise.all(
                    quiz.questionIds.map(qId => getQuestionById(qId).catch(() => null))
                );
                const mapped = questionData.filter(q => q !== null).map(mapApiQuestion);
                // Shuffle questions randomly (Fisher-Yates)
                for (let i = mapped.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [mapped[i], mapped[j]] = [mapped[j], mapped[i]];
                }
                setQuestions(mapped);
            } catch (err) {
                console.error('Error loading quiz questions:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadQuizQuestions();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (location.state?.startIndex !== undefined) {
            setCurrentQuestionIndex(location.state.startIndex);
        }
    }, [location.state?.startIndex]);

    if (loading) {
        return (
            <div className="quiz-question-page">
                <div className="quiz-question-container">
                    <p style={{ color: '#ddd', textAlign: 'center', padding: '3rem' }}>Načítání otázek...</p>
                </div>
            </div>
        );
    }

    if (error || !questions || questions.length === 0) {
        return (
            <div className="quiz-question-page">
                <div className="quiz-question-container">
                    <div className="not-found">
                        <h1>{error ? 'Chyba při načítání' : 'Kvíz nemá žádné otázky'}</h1>
                        <button onClick={() => navigate(`/kvizy/${id}`)} className="back-button">
                            Zpět na detail kvízu
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];
    const totalQuestions = questions.length;
    const isFirstQuestion = currentQuestionIndex === 0;
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    const handleAnswerSelect = (questionId, answer) => {
        setSelectedAnswers({
            ...selectedAnswers,
            [questionId]: answer
        });
    };

    const handleBack = () => {
        if (!isFirstQuestion) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleGoToSummary = () => {
        skipRef.current = true;
        navigate(`/kvizy/${id}/summary`, {
            state: { selectedAnswers, questions, returnPath: location.state?.returnPath }
        });
    };

    const handleNext = () => {
        if (isLastQuestion) {
            skipRef.current = true;
            navigate(`/kvizy/${id}/summary`, {
                state: { selectedAnswers, questions, returnPath: location.state?.returnPath }
            });
        } else {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const renderQuestion = () => {
        switch (currentQuestion.type) {
            case 'Multichoice':
                return (
                    <MultipleChoiceQuestion
                        question={currentQuestion}
                        selectedAnswer={selectedAnswers[currentQuestion.id]}
                        onAnswerSelect={(answer) => handleAnswerSelect(currentQuestion.id, answer)}
                    />
                );
            case 'MultipleCorrect':
                return (
                    <MultipleCorrectQuestion
                        question={currentQuestion}
                        selectedAnswers={selectedAnswers[currentQuestion.id]}
                        onAnswerSelect={(answer) => handleAnswerSelect(currentQuestion.id, answer)}
                    />
                );
            case 'FillIn':
                return (
                    <FillInQuestion
                        question={currentQuestion}
                        answer={selectedAnswers[currentQuestion.id]}
                        onAnswerChange={(answer) => handleAnswerSelect(currentQuestion.id, answer)}
                    />
                );
            case 'Linking':
                return (
                    <LinkingQuestion
                        question={currentQuestion}
                        pairs={selectedAnswers[currentQuestion.id]}
                        onPairsChange={(answer) => handleAnswerSelect(currentQuestion.id, answer)}
                    />
                );
            case 'Timeline':
                return (
                    <TimelineQuestion
                        question={currentQuestion}
                        order={selectedAnswers[currentQuestion.id]}
                        onOrderChange={(answer) => handleAnswerSelect(currentQuestion.id, answer)}
                    />
                );
            case 'FindInText':
                return (
                    <FindInTextQuestion
                        question={currentQuestion}
                        selectedWords={selectedAnswers[currentQuestion.id]}
                        onWordSelect={(answer) => handleAnswerSelect(currentQuestion.id, answer)}
                    />
                );
            default:
                return <div>Nepodporovaný typ otázky</div>;
        }
    };

    return (
        <div className="quiz-question-page">
            <div className="quiz-question-container">
                {/* Progress Bar */}
                <div className="progress-section">
                    <div className="progress-text">
                        Otázka {currentQuestionIndex + 1} z {totalQuestions}
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Question context */}
                {(currentQuestion.relatedBookTitle || currentQuestion.relatedAuthorName) && (
                    <div className="question-context">
                        <span className="question-context-label">Otázka se týká</span>
                        <div className="question-context-value">
                            {currentQuestion.relatedBookTitle && (
                                <span className="question-context-book">
                                    {currentQuestion.relatedBookTitle}
                                </span>
                            )}
                            {currentQuestion.relatedAuthorName && (
                                <span className="question-context-author">
                                    {currentQuestion.relatedAuthorName}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Question Content */}
                <div className="question-tile">
                    {renderQuestion()}
                </div>

                {/* Navigation Buttons */}
                <div className="navigation-buttons">
                    <button
                        onClick={handleBack}
                        className="nav-button back-button-quiz"
                        disabled={isFirstQuestion}
                    >
                        ← Zpět
                    </button>
                    <div className="navigation-right">
                        {!isLastQuestion && (
                            <button onClick={handleGoToSummary} className="skip-to-summary-btn">
                                Přeskočit na konec
                            </button>
                        )}
                        <button
                            onClick={handleNext}
                            className="nav-button next-button"
                        >
                            {isLastQuestion ? 'Dokončit' : 'Další →'}
                        </button>
                    </div>
                </div>
            </div>
            <UnsavedChangesModal
                isActive={questions !== null && questions.length > 0}
                skipRef={skipRef}
                message="Právě probíhá kvíz. Pokud odejdete, přijdete o svůj postup a všechny odpovědi."
            />
        </div>
    );
}

export default QuizQuestion;
